import React, { useEffect, useRef, useState, useCallback } from "react";
import { MessageCircle, X, Bell, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getSocket } from "@/lib/socket";
import { enablePush, pushSupported } from "@/lib/push";
import OrderChatModal from "@/components/OrderChatModal";
import { toast } from "sonner";

let _audioCtx = null;
function _getCtx() {
  if (!_audioCtx) {
    try { _audioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch { return null; }
  }
  return _audioCtx;
}
// iOS Safari only allows programmatic Web Audio after the context is unlocked by a real
// user gesture. Unlock (resume + a silent blip) on the customer's first tap so later
// chat-reply chimes can play even though they fire from a background poll.
function _unlockAudio() {
  const ctx = _getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  try {
    const o = ctx.createOscillator(), g = ctx.createGain();
    g.gain.value = 0.00001; o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 0.02);
  } catch { /* ignore */ }
}
if (typeof window !== "undefined") {
  window.addEventListener("touchend", _unlockAudio, { once: true });
  window.addEventListener("click", _unlockAudio, { once: true });
}

function _chime() {
  try {
    const ctx = _getCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const play = (freq, start, dur) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sine"; o.frequency.value = freq;
      o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
      o.start(ctx.currentTime + start); o.stop(ctx.currentTime + start + dur);
    };
    play(880, 0, 0.16); play(1180, 0.18, 0.2);
  } catch { /* audio not available */ }
}

export default function ChatWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [unread, setUnread] = useState(0);
  const [chatOrder, setChatOrder] = useState(null);
  const notified = useRef(new Set());
  const seeded = useRef(false);

  const notify = (msg) => {
    if (!msg || notified.current.has(msg.id)) return;
    notified.current.add(msg.id);
    if (!seeded.current) return; // don't ring for replies that arrived before this session
    _chime();
    if (navigator.vibrate) navigator.vibrate([120, 60, 120]);
    toast("METROS replied", {
      description: msg.order_number ? `Order ${msg.order_number}: ${msg.text}` : msg.text,
      action: { label: "Open", onClick: () => { setOpen(true); if (msg.order_number) setChatOrder({ order_number: msg.order_number }); } },
    });
  };

  const loadThreads = useCallback(async () => {
    if (!user) return;
    try { const { data } = await api.get("/chat/my-order-threads"); setThreads(data || []); }
    catch { /* ignore */ }
  }, [user]);

  useEffect(() => { if (open) loadThreads(); }, [open, loadThreads]);

  useEffect(() => {
    if (!user) return;
    const s = getSocket();
    const onMsg = (m) => {
      if (m.customer_id !== user.id) return;
      if (m.sender === "admin") notify(m);
      loadThreads();
    };
    s.on("chat_message", onMsg);
    return () => s.off("chat_message", onMsg);
    // eslint-disable-next-line
  }, [user, loadThreads]);

  // Reliable fallback: Socket.IO delivery isn't guaranteed behind the proxy, so poll for new replies.
  useEffect(() => {
    if (!user) return;
    let active = true;
    const check = async () => {
      try {
        const { data } = await api.get("/chat/unread-count");
        if (!active) return;
        setUnread(data.count || 0);
        if (data.count > 0 && data.latest) { notify(data.latest); loadThreads(); }
      } catch { /* ignore */ }
      finally { seeded.current = true; }
    };
    check();
    const t = setInterval(check, 10000);
    return () => { active = false; clearInterval(t); };
    // eslint-disable-next-line
  }, [user]);

  // Keep reminding the customer (chime + toast) every 25s while there are unread replies and
  // the chat isn't open — stops the moment they press/open it.
  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => {
      if (!open && unread > 0 && seeded.current) {
        _chime();
        if (navigator.vibrate) navigator.vibrate([120, 60, 120]);
        toast("METROS — new message", {
          id: "chat-unread-reminder",
          duration: Infinity,
          description: `You have ${unread} unread message${unread > 1 ? "s" : ""}. Tap to open.`,
          action: { label: "Open", onClick: () => setOpen(true) },
        });
      }
    }, 25000);
    return () => clearInterval(t);
  }, [user, open, unread]);

  useEffect(() => {
    if (open || unread === 0) toast.dismiss("chat-unread-reminder");
  }, [open, unread]);

  const openThread = (t) => {
    setChatOrder({ order_number: t.order_number });
    // optimistically clear this order's unread in the list
    setThreads((prev) => prev.map((x) => (x.order_number === t.order_number ? { ...x, unread: 0 } : x)));
  };

  const closeChat = () => {
    setChatOrder(null);
    loadThreads();
    api.get("/chat/unread-count").then(({ data }) => setUnread(data.count || 0)).catch(() => {});
  };

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  return (
    <>
      <button data-testid="chat-fab" onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 right-5 z-[90] w-14 h-14 rounded-full bg-metros-red text-white shadow-xl flex items-center justify-center active:scale-95 transition-transform">
        {open ? <X size={22} /> : <MessageCircle size={24} />}
        {!open && unread > 0 && (
          <span data-testid="chat-fab-unread-badge" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-white text-metros-red text-[11px] font-extrabold flex items-center justify-center border-2 border-metros-red animate-pulse">{unread}</span>
        )}
      </button>
      {open && (
        <div data-testid="chat-panel" className="fixed bottom-24 right-5 z-[90] w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden">
          <div className="bg-metros-black text-white px-4 py-3 font-heading font-bold flex items-center justify-between">
            <span>Messages</span>
            {user && pushSupported() && (
              <button data-testid="chat-enable-push" title="Get notified of replies" onClick={async () => { try { await enablePush(); toast.success("You'll be notified of replies"); } catch (e) { toast.error(e.message || "Could not enable notifications"); } }}
                className="text-white/70 hover:text-white"><Bell size={16} /></button>
            )}
          </div>
          {!user ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center">
              <p className="text-sm text-gray-600">Sign in to see messages about your orders.</p>
              <button data-testid="chat-signin" onClick={() => { setOpen(false); navigate("/login"); }}
                className="bg-metros-red text-white rounded-full px-5 py-2 font-bold text-sm">Sign in</button>
            </div>
          ) : threads.length === 0 ? (
            <div className="flex-1 flex items-center justify-center p-6 text-center text-sm text-gray-600">
              No messages yet. Start a chat from any order in <button onClick={() => { setOpen(false); navigate("/orders"); }} className="text-metros-red font-semibold underline">My orders</button>.
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto overscroll-contain p-2 bg-gray-50" data-testid="chat-order-list">
              {threads.map((t) => (
                <button key={t.order_number} data-testid={`chat-thread-${t.order_number}`} onClick={() => openThread(t)}
                  className={`w-full text-left mb-2 rounded-xl border p-3 bg-white transition-colors hover:border-metros-red ${t.unread > 0 ? "border-metros-red/40 ring-1 ring-metros-red/20" : "border-gray-100"}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="relative shrink-0">
                        {t.unread > 0 && (
                          <span data-testid={`chat-thread-unread-${t.order_number}`}
                            className="absolute -top-2 -right-3 min-w-[16px] h-4 px-1 rounded-full bg-metros-red text-white text-[9px] font-extrabold flex items-center justify-center animate-pulse">
                            {t.unread}
                          </span>
                        )}
                        <span className="font-heading font-bold text-metros-black">{t.order_number}</span>
                      </span>
                      {t.unread > 0 && <span className="text-[10px] font-bold text-metros-red uppercase tracking-wide">New message</span>}
                    </div>
                    <ChevronRight size={16} className="text-gray-400 shrink-0" />
                  </div>
                  <p className={`text-xs mt-1 truncate ${t.unread > 0 ? "text-metros-black font-semibold" : "text-muted-foreground"}`}>
                    {t.last_sender === "customer" ? "You: " : ""}{t.last_text}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {chatOrder && <OrderChatModal order={chatOrder} viewer="customer" onClose={closeChat} />}
    </>
  );
}
